import { supabase } from './supabase'

export interface BackupConfig {
  id: string
  enabled: boolean
  frequency: 'daily' | 'weekly' | 'monthly'
  retentionDays: number
  backupHour: number
  backupMinute: number
  lastBackupAt: Date | null
  nextBackupAt: Date | null
  createdAt: Date
  updatedAt: Date
}

export interface BackupHistory {
  id: string
  versionNumber: number
  backupType: 'automatic' | 'manual'
  status: 'in_progress' | 'completed' | 'failed'
  fileSize: number | null
  tablesBackedUp: string[]
  createdByUserId: string | null
  createdByUserName: string | null
  errorMessage: string | null
  createdAt: Date
  restoredAt: Date | null
  restoredByUserId: string | null
  restoredByUserName: string | null
}

const CONFIG_KEY = 'savidna-backup-config'
const HISTORY_KEY = 'savidna-backup-history'
const SUPABASE_CONFIG_KEY = 'backup_config'

// Tablas a incluir en el backup
const BACKUP_TABLES = [
  'patients',
  'doctors',
  'appointments',
  'services',
  'specialties',
  'insurance',
  'clinical_histories',
  'contacts',
  'companies',
  'opportunities',
  'products',
  'product_sales',
  'follow_up_notes',
  'audit_logs',
  'users',
  'roles',
  'permissions',
]

function generateId(): string {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Calcula la próxima fecha de backup según la frecuencia
 */
function calculateNextBackupAt(
  frequency: 'daily' | 'weekly' | 'monthly',
  fromDate?: Date,
  hour: number = 3,
  minute: number = 0,
): Date {
  const base = fromDate || new Date()
  const next = new Date(base)
  switch (frequency) {
    case 'daily':
      next.setDate(next.getDate() + 1)
      break
    case 'weekly':
      next.setDate(next.getDate() + 7)
      break
    case 'monthly':
      next.setMonth(next.getMonth() + 1)
      break
  }
  next.setHours(hour, minute, 0, 0)
  return next
}

function getDefaultConfig(): BackupConfig {
  return {
    id: generateId(),
    enabled: false,
    frequency: 'weekly',
    retentionDays: 30,
    backupHour: 3,
    backupMinute: 0,
    lastBackupAt: null,
    nextBackupAt: null,
    createdAt: new Date(),
    updatedAt: new Date(),
  }
}

// ─────────────────────────────────────────────
// CONFIG: Supabase (app_settings) + localStorage cache
// ─────────────────────────────────────────────

async function readConfigFromSupabase(): Promise<BackupConfig | null> {
  try {
    const { data, error } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', SUPABASE_CONFIG_KEY)
      .single()

    if (error || !data?.value) return null

    const val =
      typeof data.value === 'string' ? JSON.parse(data.value) : data.value
    return {
      ...val,
      backupHour: val.backupHour ?? 3,
      backupMinute: val.backupMinute ?? 0,
      lastBackupAt: val.lastBackupAt ? new Date(val.lastBackupAt) : null,
      nextBackupAt: val.nextBackupAt ? new Date(val.nextBackupAt) : null,
      createdAt: val.createdAt ? new Date(val.createdAt) : new Date(),
      updatedAt: val.updatedAt ? new Date(val.updatedAt) : new Date(),
    }
  } catch (err) {
    console.warn('[Backup] No se pudo leer config desde Supabase:', err)
    return null
  }
}

async function writeConfigToSupabase(config: BackupConfig): Promise<boolean> {
  try {
    const { error } = await supabase.from('app_settings').upsert(
      {
        key: SUPABASE_CONFIG_KEY,
        value: config,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'key' },
    )

    if (error) {
      console.warn(
        '[Backup] Error guardando config en Supabase:',
        error.message,
      )
      return false
    }
    return true
  } catch (err) {
    console.warn('[Backup] No se pudo escribir config en Supabase:', err)
    return false
  }
}

function readConfigFromLocalStorage(): BackupConfig | null {
  try {
    const stored = localStorage.getItem(CONFIG_KEY)
    if (!stored) return null
    const data = JSON.parse(stored)
    return {
      ...data,
      backupHour: data.backupHour ?? 3,
      backupMinute: data.backupMinute ?? 0,
      lastBackupAt: data.lastBackupAt ? new Date(data.lastBackupAt) : null,
      nextBackupAt: data.nextBackupAt ? new Date(data.nextBackupAt) : null,
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt),
    }
  } catch {
    return null
  }
}

function writeConfigToLocalStorage(config: BackupConfig): void {
  try {
    localStorage.setItem(CONFIG_KEY, JSON.stringify(config))
  } catch {
    // localStorage may be full or unavailable
  }
}

export async function getBackupConfig(): Promise<BackupConfig> {
  try {
    const supabaseConfig = await readConfigFromSupabase()
    if (supabaseConfig) {
      writeConfigToLocalStorage(supabaseConfig)
      return supabaseConfig
    }

    const localConfig = readConfigFromLocalStorage()
    if (localConfig) {
      writeConfigToSupabase(localConfig)
      return localConfig
    }

    const defaultConfig = getDefaultConfig()
    writeConfigToLocalStorage(defaultConfig)
    writeConfigToSupabase(defaultConfig)
    return defaultConfig
  } catch (error) {
    console.error('Error obteniendo configuración de backup:', error)
    const defaultConfig = getDefaultConfig()
    writeConfigToLocalStorage(defaultConfig)
    return defaultConfig
  }
}

export async function updateBackupConfig(
  config: Partial<BackupConfig>,
): Promise<boolean> {
  try {
    const current = await getBackupConfig()
    const newEnabled =
      config.enabled !== undefined ? config.enabled : current.enabled
    const newFrequency = config.frequency || current.frequency
    const newHour =
      config.backupHour !== undefined ? config.backupHour : current.backupHour
    const newMinute =
      config.backupMinute !== undefined
        ? config.backupMinute
        : current.backupMinute

    let nextBackupAt = current.nextBackupAt
    if (newEnabled) {
      if (
        config.enabled === true ||
        config.frequency ||
        config.backupHour !== undefined ||
        config.backupMinute !== undefined ||
        !current.nextBackupAt
      ) {
        const baseDate =
          config.lastBackupAt || current.lastBackupAt || new Date()
        nextBackupAt = calculateNextBackupAt(
          newFrequency,
          baseDate instanceof Date ? baseDate : new Date(baseDate),
          newHour,
          newMinute,
        )
      }
    } else if (config.enabled === false) {
      nextBackupAt = null
    }

    const updated: BackupConfig = {
      ...current,
      ...(config.enabled !== undefined ? { enabled: config.enabled } : {}),
      ...(config.frequency ? { frequency: config.frequency } : {}),
      ...(config.retentionDays !== undefined
        ? { retentionDays: config.retentionDays }
        : {}),
      ...(config.backupHour !== undefined
        ? { backupHour: config.backupHour }
        : {}),
      ...(config.backupMinute !== undefined
        ? { backupMinute: config.backupMinute }
        : {}),
      ...(config.lastBackupAt !== undefined
        ? { lastBackupAt: config.lastBackupAt }
        : {}),
      nextBackupAt,
      updatedAt: new Date(),
    }

    const supabaseSaved = await writeConfigToSupabase(updated)
    writeConfigToLocalStorage(updated)

    console.log('[Backup] Configuración guardada:', {
      enabled: updated.enabled,
      frequency: updated.frequency,
      backupHour: updated.backupHour,
      backupMinute: updated.backupMinute,
      nextBackupAt: updated.nextBackupAt,
      supabasePersisted: supabaseSaved,
    })
    return true
  } catch (error) {
    console.error('Error actualizando configuración de backup:', error)
    return false
  }
}

// ─────────────────────────────────────────────
// HISTORY: Supabase (backup_history) + localStorage cache
// ─────────────────────────────────────────────

/**
 * Valida si un string es un UUID válido
 */
function isValidUUID(str: string | null | undefined): boolean {
  if (!str) return false
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    str,
  )
}

/**
 * Mapea una fila de Supabase al tipo BackupHistory
 */
function mapRowToHistory(row: any): BackupHistory {
  return {
    id: row.id,
    versionNumber: row.version_number,
    backupType: row.backup_type,
    status: row.status,
    fileSize: row.file_size,
    tablesBackedUp: row.tables_backed_up ?? [],
    createdByUserId: row.created_by_user_id,
    createdByUserName: row.created_by_user_name,
    errorMessage: row.error_message,
    createdAt: new Date(row.created_at),
    restoredAt: row.restored_at ? new Date(row.restored_at) : null,
    restoredByUserId: row.restored_by_user_id,
    restoredByUserName: row.restored_by_user_name,
  }
}

/**
 * Mapea un BackupHistory al formato de fila para Supabase.
 * - created_by_user_id es uuid en la tabla → solo se envía si es UUID válido
 * - restored_by_user_id igual
 */
function mapHistoryToRow(entry: BackupHistory): Record<string, any> {
  return {
    id: entry.id,
    version_number: entry.versionNumber,
    backup_type: entry.backupType,
    status: entry.status,
    file_size: entry.fileSize,
    tables_backed_up: entry.tablesBackedUp,
    // Solo enviar UUID válidos — si el id del usuario no es UUID (ej: localStorage user), enviar null
    created_by_user_id: isValidUUID(entry.createdByUserId)
      ? entry.createdByUserId
      : null,
    created_by_user_name: entry.createdByUserName,
    error_message: entry.errorMessage,
    created_at: entry.createdAt.toISOString(),
    restored_at: entry.restoredAt ? entry.restoredAt.toISOString() : null,
    restored_by_user_id: isValidUUID(entry.restoredByUserId)
      ? entry.restoredByUserId
      : null,
    restored_by_user_name: entry.restoredByUserName,
  }
}

/**
 * Guarda una entrada de historial en Supabase backup_history.
 * Loggea el error completo para facilitar diagnóstico.
 */
async function insertHistoryToSupabase(entry: BackupHistory): Promise<boolean> {
  try {
    const row = mapHistoryToRow(entry)
    console.log('[Backup] Insertando en backup_history:', row)

    const { data, error } = await supabase
      .from('backup_history')
      .insert(row)
      .select()

    if (error) {
      // Log completo del error para diagnóstico
      console.error('[Backup] Error guardando historial en Supabase:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        row,
      })
      return false
    }

    console.log(
      `[Backup] ✅ Historial v${entry.versionNumber} guardado en Supabase:`,
      data,
    )
    return true
  } catch (err) {
    console.error('[Backup] Excepción al insertar historial en Supabase:', err)
    return false
  }
}

/**
 * Obtiene el historial de backups desde Supabase.
 * Fallback a localStorage si Supabase falla.
 */
export async function getBackupHistory(): Promise<BackupHistory[]> {
  try {
    const { data, error } = await supabase
      .from('backup_history')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      console.warn(
        '[Backup] Error leyendo historial de Supabase, usando localStorage:',
        error.message,
      )
      return getLocalHistory()
    }

    if (data && data.length > 0) {
      const history = data.map(mapRowToHistory)
      // Sincronizar cache local
      saveLocalHistory(history)
      return history
    }

    // Supabase vacío — intentar migrar desde localStorage si hay datos
    const localHistory = getLocalHistory()
    if (localHistory.length > 0) {
      console.log('[Backup] Migrando historial local a Supabase...')
      await migrateLocalHistoryToSupabase(localHistory)
      return localHistory
    }

    return []
  } catch (err) {
    console.warn(
      '[Backup] Error inesperado leyendo historial, usando localStorage:',
      err,
    )
    return getLocalHistory()
  }
}

/**
 * Migra el historial existente en localStorage a Supabase (una sola vez)
 */
async function migrateLocalHistoryToSupabase(
  history: BackupHistory[],
): Promise<void> {
  try {
    const rows = history.map(mapHistoryToRow)
    const { error } = await supabase
      .from('backup_history')
      .upsert(rows, { onConflict: 'id' })
    if (error) {
      console.warn(
        '[Backup] Error migrando historial a Supabase:',
        error.message,
      )
    } else {
      console.log(
        `[Backup] ${history.length} entradas migradas a Supabase exitosamente`,
      )
    }
  } catch (err) {
    console.warn('[Backup] No se pudo migrar historial a Supabase:', err)
  }
}

// ─── localStorage como cache/fallback ───

function getLocalHistory(): BackupHistory[] {
  try {
    const stored = localStorage.getItem(HISTORY_KEY)
    if (!stored) return []
    const data = JSON.parse(stored) as any[]
    return data.map((b) => ({
      ...b,
      createdAt: new Date(b.createdAt),
      restoredAt: b.restoredAt ? new Date(b.restoredAt) : null,
    }))
  } catch {
    return []
  }
}

function saveLocalHistory(history: BackupHistory[]): void {
  try {
    const metadataOnly = history.map((entry) => {
      const clean: any = { ...entry }
      delete clean.backupData
      return clean
    })
    localStorage.setItem(HISTORY_KEY, JSON.stringify(metadataOnly))
  } catch {
    // localStorage may be full
  }
}

// ─────────────────────────────────────────────
// BACKUP: crear, restaurar, eliminar
// ─────────────────────────────────────────────

/**
 * Descarga datos como archivo JSON
 */
function triggerDownload(data: any, versionNumber: number, date: Date): void {
  const dataStr = JSON.stringify(data, null, 2)
  const blob = new Blob([dataStr], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `savidna-backup-v${versionNumber}-${date.toISOString().split('T')[0]}.json`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Crea un backup manual o automático.
 * - Descarga los datos como archivo JSON
 * - Guarda los metadatos en Supabase backup_history (persistente)
 * - Actualiza localStorage como cache
 */
export async function createBackup(
  userId: string,
  userName: string,
  type: 'automatic' | 'manual' = 'manual',
): Promise<{ success: boolean; backupId?: string; error?: string }> {
  try {
    // Obtener número de versión desde Supabase para consistencia
    const existingHistory = await getBackupHistory()
    const maxVersion = existingHistory.reduce(
      (max, b) => Math.max(max, b.versionNumber),
      0,
    )
    const versionNumber = maxVersion + 1

    // Recopilar datos de todas las tablas desde Supabase
    const backupData: any = {}
    const tablesBackedUp: string[] = []
    let totalSize = 0

    for (const table of BACKUP_TABLES) {
      try {
        const { data, error } = await supabase.from(table).select('*')
        if (!error && data) {
          backupData[table] = data
          tablesBackedUp.push(table)
          totalSize += JSON.stringify(data).length
        }
      } catch (err) {
        console.warn(`No se pudo hacer backup de la tabla ${table}:`, err)
      }
    }

    const now = new Date()

    // Descargar el archivo JSON inmediatamente
    triggerDownload(backupData, versionNumber, now)

    // Crear entrada de historial
    const newEntry: BackupHistory = {
      id: generateId(),
      versionNumber,
      backupType: type,
      status: 'completed',
      fileSize: totalSize,
      tablesBackedUp,
      createdByUserId: userId,
      createdByUserName: userName,
      errorMessage: null,
      createdAt: now,
      restoredAt: null,
      restoredByUserId: null,
      restoredByUserName: null,
    }

    // Guardar en Supabase (fuente de verdad)
    const savedToSupabase = await insertHistoryToSupabase(newEntry)

    // Actualizar cache local
    const localHistory = getLocalHistory()
    localHistory.unshift(newEntry)
    saveLocalHistory(localHistory)

    if (!savedToSupabase) {
      console.warn(
        '[Backup] Historial guardado solo en localStorage (Supabase falló)',
      )
    }

    // Actualizar config con última fecha de backup
    await updateBackupConfig({ lastBackupAt: now })

    console.log(
      `[Backup] Backup v${versionNumber} creado (${type}). Tablas: ${tablesBackedUp.length}. Supabase: ${savedToSupabase}`,
    )

    return { success: true, backupId: newEntry.id }
  } catch (error: any) {
    console.error('Error creando backup:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Restaura un backup desde un archivo JSON subido por el usuario.
 * Registra la restauración en Supabase backup_history.
 */
export async function restoreBackupFromFile(
  backupData: Record<string, any[]>,
  userId: string,
  userName: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    // Restaurar cada tabla en Supabase
    for (const [tableName, tableData] of Object.entries(backupData)) {
      if (!Array.isArray(tableData)) continue

      try {
        await supabase
          .from(tableName)
          .delete()
          .neq('id', '00000000-0000-0000-0000-000000000000')

        if (tableData.length > 0) {
          await supabase.from(tableName).insert(tableData)
        }
      } catch (err) {
        console.warn(`Error restaurando tabla ${tableName}:`, err)
      }
    }

    // Registrar la restauración en historial
    const existingHistory = await getBackupHistory()
    const maxVersion = existingHistory.reduce(
      (max, b) => Math.max(max, b.versionNumber),
      0,
    )

    const now = new Date()
    const restoreEntry: BackupHistory = {
      id: generateId(),
      versionNumber: maxVersion + 1,
      backupType: 'manual',
      status: 'completed',
      fileSize: JSON.stringify(backupData).length,
      tablesBackedUp: Object.keys(backupData),
      createdByUserId: userId,
      createdByUserName: userName,
      errorMessage: null,
      createdAt: now,
      restoredAt: now,
      restoredByUserId: userId,
      restoredByUserName: userName,
    }

    // Guardar en Supabase (fuente de verdad)
    await insertHistoryToSupabase(restoreEntry)

    // Actualizar cache local
    const localHistory = getLocalHistory()
    localHistory.unshift(restoreEntry)
    saveLocalHistory(localHistory)

    return { success: true }
  } catch (error: any) {
    console.error('Error restaurando backup:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Elimina backups antiguos según la política de retención.
 * Elimina de Supabase y de localStorage.
 */
export async function cleanOldBackups(retentionDays: number): Promise<number> {
  try {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - retentionDays)
    const cutoffISO = cutoffDate.toISOString()

    // Eliminar de Supabase
    const { data: deleted, error } = await supabase
      .from('backup_history')
      .delete()
      .lt('created_at', cutoffISO)
      .select('id')

    if (error) {
      console.warn(
        '[Backup] Error limpiando backups en Supabase:',
        error.message,
      )
    }

    const deletedCount = deleted?.length ?? 0

    // Sincronizar localStorage
    const localHistory = getLocalHistory()
    const toKeep = localHistory.filter(
      (b) => new Date(b.createdAt).getTime() >= cutoffDate.getTime(),
    )
    saveLocalHistory(toKeep)

    console.log(`[Backup] ${deletedCount} backup(s) antiguos eliminados`)
    return deletedCount
  } catch (error) {
    console.error('Error limpiando backups antiguos:', error)

    // Fallback: limpiar solo localStorage
    const localHistory = getLocalHistory()
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - retentionDays)
    const toKeep = localHistory.filter(
      (b) => new Date(b.createdAt).getTime() >= cutoffDate.getTime(),
    )
    const deletedCount = localHistory.length - toKeep.length
    saveLocalHistory(toKeep)
    return deletedCount
  }
}

/**
 * Elimina un backup específico de Supabase y localStorage.
 */
export async function deleteBackup(backupId: string): Promise<boolean> {
  try {
    // Eliminar de Supabase
    const { error } = await supabase
      .from('backup_history')
      .delete()
      .eq('id', backupId)

    if (error) {
      console.warn('[Backup] Error eliminando de Supabase:', error.message)
    }

    // Eliminar de localStorage cache
    const localHistory = getLocalHistory()
    saveLocalHistory(localHistory.filter((b) => b.id !== backupId))

    return !error
  } catch (error) {
    console.error('Error eliminando backup:', error)
    return false
  }
}

/**
 * Formatea el tamaño del archivo
 */
export function formatFileSize(bytes: number | null): string {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
}

/**
 * Verifica si es momento de ejecutar un backup automático.
 * Incluye guard contra ejecuciones concurrentes y verificación server-side de rol admin.
 */
export async function checkAndRunAutoBackup(
  userId: string,
  userName: string,
): Promise<boolean> {
  // Prevent concurrent executions
  if (isAutoBackupRunning) {
    console.log('[Backup] Auto-backup ya en ejecución, omitiendo...')
    return false
  }

  try {
    isAutoBackupRunning = true

    // Server-side admin check: verify user is admin in Supabase
    // This prevents non-admin users with old cached code from triggering backups
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single()

      if (userError || !userData) {
        console.warn(
          '[Backup] No se pudo verificar rol del usuario, omitiendo backup',
        )
        return false
      }

      if (userData.role !== 'administrador') {
        console.log(
          `[Backup] Usuario ${userName} no es administrador (${userData.role}), omitiendo backup`,
        )
        return false
      }
    } catch (roleErr) {
      console.warn('[Backup] Error verificando rol, omitiendo backup:', roleErr)
      return false
    }

    // Re-read config from Supabase to get the latest state (another tab/user may have updated it)
    const config = await getBackupConfig()

    if (!config.enabled) return false
    if (!config.nextBackupAt) {
      await updateBackupConfig({ enabled: true })
      return false
    }

    const now = new Date()
    const nextBackup = new Date(config.nextBackupAt)

    // If nextBackupAt is in the future, nothing to do
    if (now < nextBackup) {
      return false
    }

    // Cooldown: check lastBackupAt from config (persisted in Supabase, shared across users/tabs)
    if (config.lastBackupAt) {
      const lastBackup = new Date(config.lastBackupAt)
      const minutesSinceLastBackup =
        (now.getTime() - lastBackup.getTime()) / (1000 * 60)
      if (minutesSinceLastBackup < 10) {
        console.log(
          `[Backup] Último backup hace ${minutesSinceLastBackup.toFixed(1)} min, omitiendo (cooldown 10 min)`,
        )
        // nextBackupAt is in the past but a recent backup exists — recalculate next
        await updateBackupConfig({
          lastBackupAt: lastBackup,
        })
        return false
      }
    }

    console.log('[Backup] Ejecutando backup automático programado...')
    const result = await createBackup(userId, userName, 'automatic')
    if (result.success) {
      await cleanOldBackups(config.retentionDays)
      console.log('[Backup] Backup automático completado exitosamente')
      return true
    } else {
      console.error('[Backup] Error en backup automático:', result.error)
    }

    return false
  } catch (error) {
    console.error('[Backup] Error verificando backup automático:', error)
    return false
  } finally {
    isAutoBackupRunning = false
  }
}

// Auto-backup scheduler (singleton)
let autoBackupInterval: ReturnType<typeof setInterval> | null = null
let isAutoBackupRunning = false

/**
 * Inicia el scheduler de backups automáticos.
 * Verifica cada 60 segundos si es momento de ejecutar un backup.
 */
export function startAutoBackupScheduler(
  userId: string,
  userName: string,
): void {
  if (autoBackupInterval) {
    clearInterval(autoBackupInterval)
  }

  console.log('[Backup] Scheduler de backups automáticos iniciado')

  // Check immediately on start
  checkAndRunAutoBackup(userId, userName)

  // Then check every 60 seconds
  autoBackupInterval = setInterval(() => {
    checkAndRunAutoBackup(userId, userName)
  }, 60 * 1000)
}

/**
 * Detiene el scheduler de backups automáticos
 */
export function stopAutoBackupScheduler(): void {
  if (autoBackupInterval) {
    clearInterval(autoBackupInterval)
    autoBackupInterval = null
    console.log('[Backup] Scheduler de backups automáticos detenido')
  }
}
